import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import {formatNumberForDate, getDaysOfBirth, getMonthsOfBirth, getYearsOfBirth} from 'coreUtils/dates';

import Field from 'brandComponents/Form/Subcomponents/Field';
import Typography from 'brandComponents/Typography';

import {IBirthDateFieldsProps, IYearsOfBirthRange} from './IBirthDateFields';
import messages from './messages';

// eslint-disable-next-line no-restricted-imports
import './BirthDateFields.scss';

const BirthDateFields = ({
	disabled,
	classNames,
	name,
	label,
	error,
	values,
	customIcon = null,
	yearsOfBirthRange
}: IBirthDateFieldsProps) => {
	const intl = useIntl();
	const defaultYearsOfBirthRange: IYearsOfBirthRange = {
		start: 6,
		end: 100
	};

	const getYearsOfBirthRange = useMemo(() => {
		const {start, end} = yearsOfBirthRange ?? defaultYearsOfBirthRange;

		return getYearsOfBirth(start, end);
	}, []);

	const dateOptions = useMemo(() => {
		return {
			days: getDaysOfBirth(),
			months: getMonthsOfBirth(),
			years: getYearsOfBirthRange,
		};
	}, []);

	const getFieldValue = (currentName: string) => {
		if (typeof values === 'string') {
			const valuesObj = {day: '', month: '', year: ''};
			const initialBirthdayDate: Date = new Date(values);

			valuesObj.day = formatNumberForDate(initialBirthdayDate.getDate());
			valuesObj.month = formatNumberForDate(initialBirthdayDate.getMonth() + 1);
			valuesObj.year = initialBirthdayDate.getFullYear().toString();

			return valuesObj[currentName];
		}

		return values && values[currentName];
	};

	const getClassNames = (currentName: string) => {
		let classes = '';
		const value = getFieldValue(currentName);

		if (error) {
			classes += ' invalid';
		}

		if (value) {
			classes += ' has-value';
		}

		if (typeof value === 'undefined' || !value) {
			classes += ' default';
		}

		return classes;
	};

	return (
		<div className={cn('birthdate-fields', classNames)}>
			{label && <Typography classNames="birthdate-label">{label}</Typography>}
			<div className="fields-date">
				<Field
					name={`${name}.day`}
					component="select"
					disabled={disabled}
					classNames={getClassNames('day')}
					customIcon={customIcon}
					initialValue={getFieldValue('day')}
				>
					<option value="">{intl.formatMessage(messages.day)}</option>
					{dateOptions.days.map((day) => (
						<option key={day.value} value={day.value}>
							{day.label}
						</option>
					))}
				</Field>
				<Field
					name={`${name}.month`}
					component="select"
					disabled={disabled}
					classNames={getClassNames('month')}
					customIcon={customIcon}
					initialValue={getFieldValue('month')}
				>
					<option value="">{intl.formatMessage(messages.month)}</option>
					{dateOptions.months.map((month) => (
						<option key={month.value} value={month.value}>
							{month.label}
						</option>
					))}
				</Field>
				<Field
					name={`${name}.year`}
					component="select"
					disabled={disabled}
					classNames={getClassNames('year')}
					customIcon={customIcon}
					initialValue={getFieldValue('year')}
				>
					<option value="">{intl.formatMessage(messages.year)}</option>
					{dateOptions.years.map((year) => (
						<option key={year.value} value={year.value}>
							{year.label}
						</option>
					))}
				</Field>
			</div>
			{error && <Typography classNames="field__error invalid">{error}</Typography>}
		</div>
	);
};

export default BirthDateFields;
